import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import {
  CALIBRATION_FOCUS_PARAM,
  useDbProjectTarget,
  useFilters,
  useGridState,
  useImageOrder,
  useLatestSearchParams,
} from './useUrlState';
import type { Image } from '../api/types';
import { imageGroupKey, resolveExpandedGroups } from '../utils/imageGrouping';
import { useGridGroups } from './useGridGroups';
import { useProjectMosaic } from './useProjectMosaic';
import {
  imageDetailClosePath,
  imageDetailPath,
  imageDetailReturnView,
} from '../utils/imageDetailRoutes';
import { matchesStatusFilter } from '../utils/statusFilter';
import { matchesFlagFilter } from '../utils/flagFilter';
import { matchesCalibrationFilter } from '../utils/calibrationFilter';
import { useScopedQuality } from './useSequenceAnalysis';
import { useCalibrationMarks } from './useCalibrationMarks';
import { useFlatGroupLights } from './useFlatGroupLights';
import { useNavigateNow } from './useNavigateNow';
import { keepAcrossNightSplit, useNightSplit } from './useNightSplit';

/**
 * Hook for navigating between images in the current context
 */
export function useImageNavigation(currentImageId?: number) {
  const location = useLocation();
  // Each move hands the keyboard to the view it opens; see useNavigateNow.
  const navigate = useNavigateNow();
  // Each move starts from the URL as it stands, so a write made in the
  // same moment, such as a grade's or a selection's, goes with it.
  const latestParams = useLatestSearchParams();
  const { dbId, projectId, targetId, mosaic: mosaicScope } = useDbProjectTarget();
  const { filters } = useFilters();
  const { groupingMode, expandedGroups } = useGridState();
  const { order } = useImageOrder();
  // In a mosaic's scope the grid lists frames under their panels; walk
  // them the same way.
  const { data: projectMosaic } = useProjectMosaic(mosaicScope ? dbId : null, mosaicScope ? projectId : null);
  const mosaic = mosaicScope ? projectMosaic ?? null : null;
  // The grid filters by quality flag too, so keyboard navigation reads
  // the same scoped quality results the grid does. Without a flag filter
  // the analysis is not fetched: the detail view must not pay for a
  // sequence analysis it does not use.
  const quality = useScopedQuality(
    dbId,
    projectId,
    targetId,
    filters.filterNames.length === 1 ? filters.filterNames[0] : undefined,
    filters.flags.length > 0,
  );
  // And by calibration, read only when that filter is on.
  const calibration = useCalibrationMarks(dbId, projectId, filters.calibration.length > 0);
  // And by one flat group's lights.
  const flatGroup = useFlatGroupLights(dbId, filters.flatGroup);

  // Fetch all images for navigation context
  // Nights as the view splits them, the server's setting or the link's.
  const nightSplit = useNightSplit();
  const { data: allImages = [] } = useQuery({
    queryKey: ['db', dbId, 'all-images', projectId, targetId, nightSplit.queryKey],
    queryFn: () =>
      apiClient.getImages(dbId!, {
        project_id: projectId!,
        target_id: targetId || undefined,
        limit: 10000,
        nights: nightSplit.override ?? undefined,
      }),
    enabled: !!dbId && !!projectId,
    // A new night split keeps the old frames on screen until its own
    // arrive, so the grid neither empties nor loses its place.
    placeholderData: keepAcrossNightSplit(['db', dbId, 'all-images', projectId, targetId]),
  });

  // Apply the same filtering logic as GroupedImageGrid
  const filteredImages = useMemo(() => {
    return allImages.filter(image => {
      // Status filter
      if (!matchesStatusFilter(filters.status, image.grading_status)) {
        return false;
      }
      
      // Filter name filter
      if (filters.filterNames.length > 0 && !filters.filterNames.includes(image.filter_name ?? '')) {
        return false;
      }
      
      // Date range filter
      if (filters.dateRange.start && image.acquired_date) {
        const imageDate = new Date(image.acquired_date * 1000);
        const startDate = new Date(filters.dateRange.start);
        if (imageDate < startDate) return false;
      }
      if (filters.dateRange.end && image.acquired_date) {
        const imageDate = new Date(image.acquired_date * 1000);
        const endDate = new Date(filters.dateRange.end);
        if (imageDate > endDate) return false;
      }
      
      // Search filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        if (!image.target_name.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Quality flag filter, once the analysis is in; until then, or if it
      // failed, navigation walks the unfiltered set rather than none.
      if (filters.flags.length > 0 && !quality.isLoading && !quality.error
        && !matchesFlagFilter(filters.flags, quality.qualityByImage.get(image.id))) {
        return false;
      }

      if (filters.calibration.length > 0 && !calibration.isLoading && !calibration.error
        && !matchesCalibrationFilter(filters.calibration, calibration.marks.get(image.id))) {
        return false;
      }

      // One flat group's lights: none until they are known, so next and
      // previous never step outside the group.
      if (flatGroup.active && (!flatGroup.ready || !flatGroup.ids.has(image.id))) {
        return false;
      }
      
      return true;
    });
  }, [
    allImages,
    filters,
    quality.isLoading,
    quality.error,
    quality.qualityByImage,
    calibration.isLoading,
    calibration.error,
    calibration.marks,
    flatGroup.active,
    flatGroup.ready,
    flatGroup.ids,
  ]);

  // The grid's own groups, in the order it shows them, by the same helper
  // and the same night boundary. Until a date grouping's boundary is in,
  // there is nothing to walk rather than a walk the grid does not show.
  const { groupsIn, ready: groupsReady } = useGridGroups(dbId, filteredImages, groupingMode, mosaic);
  const imageGroups = useMemo(
    () => groupsReady ? groupsIn(order) : [],
    [groupsIn, groupsReady, order],
  );

  const visibleExpandedGroups = useMemo(
    () => resolveExpandedGroups(imageGroups, groupingMode, expandedGroups),
    [imageGroups, groupingMode, expandedGroups],
  );

  // Create flat list respecting expanded groups (same as GroupedImageGrid)
  const flatImages = useMemo(() => {
    const result: Image[] = [];
    imageGroups.forEach((group) => {
      if (visibleExpandedGroups.has(imageGroupKey(group))) {
        result.push(...group.images);
      }
    });
    return result;
  }, [imageGroups, visibleExpandedGroups]);

  // Find current image index in the flat list
  const currentIndex = useMemo(() => {
    if (!currentImageId || flatImages.length === 0) return -1;
    return flatImages.findIndex(img => img.id === currentImageId);
  }, [currentImageId, flatImages]);

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex >= 0 && currentIndex < flatImages.length - 1;

  const navigateToImageWithContext = useCallback((imageId: number, view: 'detail' | 'comparison' = 'detail') => {
    const searchParams = latestParams();
    const params = searchParams.toString();

    if (view === 'detail') {
      // A chosen calibration finding stays chosen from image to image.
      navigate(
        imageDetailPath(
          imageId,
          searchParams,
          imageDetailReturnView(searchParams),
          searchParams.get(CALIBRATION_FOCUS_PARAM) ?? undefined,
        ),
        { replace: true, state: location.state },
      );
    } else {
      // For comparison, we need both left and right image IDs
      const rightImageId = currentImageId === imageId ? 
        (canGoNext ? flatImages[currentIndex + 1]?.id : imageId) : imageId;
      navigate(`/compare/${imageId}/${rightImageId}?${params}`, { replace: true });
    }
  }, [navigate, latestParams, currentImageId, canGoNext, flatImages, currentIndex, location.state]);

  const goToNext = useCallback(() => {
    if (canGoNext && currentIndex >= 0) {
      const nextImage = flatImages[currentIndex + 1];
      navigateToImageWithContext(nextImage.id, 'detail');
    }
  }, [canGoNext, currentIndex, flatImages, navigateToImageWithContext]);

  const goToPrevious = useCallback(() => {
    if (canGoPrevious && currentIndex >= 0) {
      const prevImage = flatImages[currentIndex - 1];
      navigateToImageWithContext(prevImage.id, 'detail');
    }
  }, [canGoPrevious, currentIndex, flatImages, navigateToImageWithContext]);

  const goToGrid = useCallback(() => {
    const params = latestParams();
    params.delete(CALIBRATION_FOCUS_PARAM);
    navigate(`/grid?${params}`, { replace: true });
  }, [latestParams, navigate]);

  const closeDetail = useCallback(() => {
    navigate(imageDetailClosePath(latestParams()), {
      replace: true,
      state: location.state,
    });
  }, [latestParams, location.state, navigate]);

  return {
    canGoPrevious,
    canGoNext,
    currentIndex,
    totalImages: flatImages.length,
    goToNext,
    goToPrevious,
    goToGrid,
    closeDetail,
    navigateToImageWithContext,
    allImages: flatImages, // Return the grouped/filtered list for compatibility
  };
}
